import React, { useState, useRef } from 'react';
import './OrchestratorUpload.css';

interface Step {
  step: string;
  type: 'tool' | 'agent';
  status: string;
  output: string;
}

interface OrchestratorResponse {
  result: string;
  steps: Step[];
  file_info: {
    filename: string;
    size: number;
    content_type: string;
  };
}

export const OrchestratorUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [response, setResponse] = useState<OrchestratorResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResponse(null);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setResponse(null);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const processFile = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setExpandedSteps(new Set([0, 1, 2, 3, 4])); // Expand all steps

    const apiUrl = process.env.NODE_ENV === 'production'
      ? 'https://ceaustin117-personal-website-backend.hf.space/orchestrator'
      : 'http://localhost:7860/orchestrator';

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`API error: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      setResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleStep = (index: number) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const resetUpload = () => {
    setFile(null);
    setResponse(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="orchestrator-upload">
      {!response ? (
        <>
          <div
            className={`upload-zone ${file ? 'has-file' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".txt,.md,.pdf,.doc,.docx"
              hidden
            />
            {file ? (
              <div className="file-preview">
                <div className="file-name">{file.name}</div>
                <div className="file-size">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div className="upload-prompt">
                <div className="upload-icon">+</div>
                <p>Drop a document here or click to browse</p>
                <span className="upload-hint">Supports .txt, .md, .pdf, .doc, .docx</span>
              </div>
            )}
          </div>

          {file && (
            <div className="upload-actions">
              <button
                className="process-btn"
                onClick={processFile}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Analyze Document'}
              </button>
              <button className="clear-btn" onClick={resetUpload}>
                Clear
              </button>
            </div>
          )}

          {isProcessing && (
            <div className="processing-indicator">
              <div className="processing-spinner"></div>
              <p>Orchestrator agent is analyzing your document...</p>
            </div>
          )}
        </>
      ) : (
        <div className="orchestrator-results">
          <div className="results-header">
            <h3>Compliance Review Complete</h3>
            <button className="new-upload-btn" onClick={resetUpload}>
              Upload New Document
            </button>
          </div>

          <div className="file-info">
            <span className="info-label">Document:</span>
            <span className="info-value">{response.file_info.filename}</span>
            <span className="info-size">({(response.file_info.size / 1024).toFixed(1)} KB)</span>
          </div>

          <div className="pipeline-steps">
            <h4>Pipeline Execution</h4>
            {response.steps.map((step, index) => (
              <div key={index} className={`pipeline-step ${step.type}`}>
                <button
                  className="step-header"
                  onClick={() => toggleStep(index)}
                >
                  <span className={`step-type-badge ${step.type}`}>
                    {step.type === 'agent' ? 'Agent' : 'Tool'}
                  </span>
                  <span className="step-name">{step.step}</span>
                  <span className={`step-toggle ${expandedSteps.has(index) ? 'expanded' : ''}`}>
                    ▶
                  </span>
                </button>
                {expandedSteps.has(index) && (
                  <div className="step-output">
                    <pre>{step.output}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="final-report">
            <h4>Final Report</h4>
            <pre className="report-content">{response.result}</pre>
          </div>
        </div>
      )}

      {error && (
        <div className="upload-error">
          <p>{error}</p>
          <button onClick={resetUpload}>Try Again</button>
        </div>
      )}
    </div>
  );
};

export default OrchestratorUpload;
